/* eslint-disable */
/**
 * serveurStoreDB - persistance du module Serveur (prise de commande en salle).
 *
 * Propriétaire unique de tout ce qui appartient en propre au serveur :
 * son identité, et les commandes qu'il prend pendant son service.
 *
 * CE QU'IL NE GÈRE PAS, volontairement : le catalogue. Les articles vivent
 * dans sgArticles/sgCatalogueMeta, partagés avec /menu et /panier - un seul
 * téléchargement pour les trois modules. Ce service les lit à travers
 * hsigscangoIndexdb, il ne les duplique ni ne les purge jamais.
 *
 * Conventions reprises de hsigscangoIndexdb (elles ne sont pas cosmétiques) :
 *
 *  - keyPath EN CLAIR, valeur sensible chiffrée dans un champ `blob`. Le
 *    chiffrement CryptoJS en mode passphrase n'est pas déterministe (salt et
 *    IV aléatoires) : une clé chiffrée serait introuvable par get().
 *  - Jamais de champ nommé `clé` : getData/deleteData de indexedDBService
 *    tenteraient un decryptData() dessus.
 *  - Les champs de pilotage (scope, statut, numero, dates) restent en clair
 *    pour être filtrables par index sans déchiffrer toute la file.
 *
 * ⚠️ Sur la confidentialité : VUE_APP_SECRET_KEY_INDEXDB est embarquée dans le
 * bundle, donc lisible par quiconque ouvre les sources. Le chiffrement protège
 * de l'inspection casuelle d'IndexedDB, PAS d'un attaquant. Le « code système »
 * du serveur ne doit pas être traité comme un mot de passe.
 */

import { initDB, encryptData, decryptData } from "@/service/indexedDBService";
import { scopeKey } from "@/service/hsigscangoIndexdb";
import { getNextWaitNumber } from "@/service/orderQueueService";

const STORE_IDENTITE = "svIdentite";
const STORE_COMMANDES = "svCommandes";

const IDENTITE_KEY = "serveur";

// Un service peut chevaucher minuit (fermeture tardive) : une commande ne doit
// pas s'évaporer en cours de repas. Le vrai nettoyage est « Clôturer mon
// service », ce TTL n'est qu'un garde-fou contre l'accumulation.
const TTL_COMMANDE = 24 * 3600 * 1000;
const TTL_IDENTITE = 30 * 24 * 3600 * 1000;

/**
 * Déchiffre sans détruire.
 *
 * hsigscangoIndexdb purge l'enregistrement quand le blob est illisible : pour
 * un catalogue c'est de l'auto-guérison, il sera retéléchargé. Ici ce serait
 * une perte sèche - une commande ne se re-télécharge pas. On journalise, on
 * écarte l'enregistrement de la liste, et on le laisse en base pour analyse.
 */
function lireBlob(cle, blob) {
    try {
        const valeur = decryptData(blob);
        if (valeur === null) {
            console.error(`[srv] blob illisible (conservé pour analyse) : ${cle}`);
            return null;
        }
        return valeur;
    } catch (e) {
        console.error(`[srv] déchiffrement impossible : ${cle}`, e);
        return null;
    }
}

function estPerime(rec) {
    return !!(rec && rec.expiretime && rec.expiretime < Date.now());
}

/* ------------------------------------------------------------- identité */

async function saveIdentite(identite) {
    const db = await initDB();
    await db.put(STORE_IDENTITE, {
        k: IDENTITE_KEY,
        blob: encryptData(identite || {}),
        updatedAt: Date.now(),
        expiretime: Date.now() + TTL_IDENTITE,
    });
    return true;
}

async function getIdentite() {
    const db = await initDB();
    const rec = await db.get(STORE_IDENTITE, IDENTITE_KEY);
    if (!rec) return null;
    if (estPerime(rec)) {
        await db.delete(STORE_IDENTITE, IDENTITE_KEY);
        return null;
    }
    return lireBlob(IDENTITE_KEY, rec.blob);
}

async function clearIdentite() {
    const db = await initDB();
    await db.delete(STORE_IDENTITE, IDENTITE_KEY);
}

/* ------------------------------------------------------------ commandes */

/**
 * Découpe une commande en « enveloppe en clair » + « contenu chiffré ».
 * Le contenu (table, lignes) porte les prix et les notes de cuisine ; le reste
 * sert à filtrer et à afficher la liste sans tout déchiffrer.
 */
function versEnregistrement(soc, mag, commande) {
    return {
        k: String(commande.id),
        scope: scopeKey(soc, mag),
        // Propriétaire de la commande, EN CLAIR pour filtrer sans déchiffrer.
        // Un téléphone peut être prêté à un collègue le temps d'un dépannage :
        // isoler les commandes par pid serveur évite de mélanger leurs additions.
        // Source : le collaborateur rattaché à la commande (pid cltfrs réel).
        serveurPid: String((commande.collaborateur && commande.collaborateur.pid) || commande.serveurPid || ""),
        statut: String(commande.statut || "ouverte"),
        numero: String(commande.numero || ""),
        creeeLe: commande.creeeLe || Date.now(),
        blob: encryptData({
            table: commande.table || null,
            // Client sur la table ({num, nom}) : plusieurs clients indépendants
            // peuvent partager une même table. Chiffré avec le reste, restitué tel quel.
            client: commande.client || null,
            // État du poll d'encaissement ({count, epuise}) : persisté pour
            // reprendre la vérification après une fermeture d'appli.
            verif: commande.verif || null,
            // Référence de l'encaissement confirmé ({docnum, montant}).
            encaissement: commande.encaissement || null,
            lignes: commande.lignes || [],
            // Le collaborateur qui a pris la commande, sous la forme attendue
            // par la caisse ({pid, name}). Chiffré comme le reste du contenu :
            // il n'a pas à être filtrable, seulement restitué.
            collaborateur: commande.collaborateur || null,
        }),
        updatedAt: Date.now(),
        expiretime: Date.now() + TTL_COMMANDE,
    };
}

/** Pid du serveur propriétaire d'un enregistrement (clair d'abord, blob en repli legacy). */
function proprietaire(rec, contenu) {
    const clair = String((rec && rec.serveurPid) || "");
    if (clair) return clair;
    const c = contenu && contenu.collaborateur;
    return String((c && c.pid) || "");
}

function depuisEnregistrement(rec) {
    const contenu = lireBlob(rec.k, rec.blob);
    if (!contenu) return null;
    return {
        id: rec.k,
        numero: rec.numero,
        statut: rec.statut,
        creeeLe: rec.creeeLe,
        table: contenu.table || null,
        client: contenu.client || null,
        verif: contenu.verif || null,
        encaissement: contenu.encaissement || null,
        lignes: Array.isArray(contenu.lignes) ? contenu.lignes : [],
        collaborateur: contenu.collaborateur || null,
    };
}

/** Upsert : la clé est l'id de la commande, une reprise écrase la version précédente. */
async function saveCommande(soc, mag, commande) {
    if (!commande || !commande.id) return false;
    const db = await initDB();
    await db.put(STORE_COMMANDES, versEnregistrement(soc, mag, commande));
    return true;
}

/**
 * Toutes les commandes d'un magasin, la plus récente d'abord.
 *
 * Passe par scopeIndex avec une plage restreinte : seules les lignes du scope
 * courant sont visitées, jamais toute la file.
 */
async function getCommandes(soc, mag, serveurPid) {
    const db = await initDB();
    const scope = scopeKey(soc, mag);
    const bruts = await db.getAllFromIndex(STORE_COMMANDES, "scopeIndex", IDBKeyRange.only(scope));

    const cible = String(serveurPid || "");
    const sorties = [];
    for (const rec of bruts) {
        if (estPerime(rec)) {
            await db.delete(STORE_COMMANDES, rec.k);
            continue;
        }
        const c = depuisEnregistrement(rec);
        if (!c) continue;         // un blob illisible est écarté, pas supprimé
        // Isolation par serveur : quand un pid est demandé, on n'expose que SES
        // commandes. Un enregistrement sans propriétaire connu (legacy, ou créé
        // sans identité) reste visible - ne pas perdre de commande orpheline.
        if (cible) {
            const owner = proprietaire(rec, c);
            if (owner && owner !== cible) continue;
        }
        sorties.push(c);
    }
    return sorties.sort((a, b) => (b.creeeLe || 0) - (a.creeeLe || 0));
}

async function getCommande(id) {
    const db = await initDB();
    const rec = await db.get(STORE_COMMANDES, String(id));
    if (!rec || estPerime(rec)) return null;
    return depuisEnregistrement(rec);
}

async function supprimerCommande(id) {
    const db = await initDB();
    await db.delete(STORE_COMMANDES, String(id));
}

/**
 * Clôture de service : on vide les commandes du magasin courant.
 *
 * Si un `serveurPid` est fourni, on ne vide QUE les commandes de ce serveur (et
 * les orphelines sans propriétaire, qui lui étaient présentées) : sur un
 * téléphone partagé, clôturer son service ne doit pas effacer celui d'un collègue.
 * Sans pid, comportement historique : on vide tout le magasin.
 */
async function clearCommandes(soc, mag, serveurPid) {
    const db = await initDB();
    const scope = scopeKey(soc, mag);
    const cible = String(serveurPid || "");
    const tx = db.transaction(STORE_COMMANDES, "readwrite");
    const index = tx.store.index("scopeIndex");
    let cursor = await index.openCursor(IDBKeyRange.only(scope));
    const cles = [];
    while (cursor) {
        const rec = cursor.value;
        if (!cible) {
            cles.push(cursor.primaryKey);
        } else {
            // On lit le propriétaire en clair d'abord ; en repli (legacy), le blob.
            let owner = String((rec && rec.serveurPid) || "");
            if (!owner) {
                const contenu = lireBlob(rec.k, rec.blob);
                owner = proprietaire(rec, contenu);
            }
            if (!owner || owner === cible) cles.push(cursor.primaryKey);
        }
        cursor = await cursor.continue();
    }
    for (const k of cles) tx.store.delete(k);
    await tx.done;
    return cles.length;
}

/**
 * Supprime les commandes des AUTRES magasins.
 *
 * Un serveur muté d'un établissement à l'autre accumulerait sinon des scopes
 * morts. Appelé après l'obtention de l'accès, jamais avant : sur la seule foi
 * de l'URL, on effacerait le travail en cours d'un scope légitime.
 */
async function purgerScopesObsoletes(keepSoc, keepMag) {
    const db = await initDB();
    const garder = scopeKey(keepSoc, keepMag);
    const tx = db.transaction(STORE_COMMANDES, "readwrite");
    let cursor = await tx.store.openCursor();
    const cles = [];
    while (cursor) {
        if (cursor.value && cursor.value.scope !== garder) cles.push(cursor.primaryKey);
        cursor = await cursor.continue();
    }
    for (const k of cles) tx.store.delete(k);
    await tx.done;
    return cles.length;
}

/* --------------------------------------------------------- numérotation */

/**
 * Numéro d'attente au format W-JJMM-NNN.
 *
 * Délègue à orderQueueService : le compteur journalier et l'historique
 * existent déjà et sont partagés avec la caisse. `meta` est répandu dans
 * l'historique - y passer le code du serveur permet de retrouver qui a pris
 * quel numéro.
 */
async function prochainNumeroAttente(meta) {
    return getNextWaitNumber(meta || {});
}

/* --------------------------------------------------- sauvegarde backend

   Photo CHIFFRÉE du store d'un serveur (identité + ses commandes), destinée à
   être poussée au backend puis rechargée en cas de perte du store local (mise à
   jour de la PWA, panne/changement de téléphone). Le backend ne voit qu'un
   ciphertext opaque : le chiffrement se fait ici, avec la même clé que le reste
   du store. La donnée suit le PID serveur, pas l'appareil. */

/**
 * Produit le ciphertext du store du serveur `serveurPid` sur ce magasin.
 * @returns {Promise<string|null>} ciphertext, ou null s'il n'y a rien à sauver.
 */
async function exporterStore(soc, mag, serveurPid) {
    const pid = String(serveurPid || "");
    if (!pid) return null;

    const commandes = await getCommandes(soc, mag, pid);
    let identite = null;
    try {
        const id = await getIdentite();
        if (id && String(id.pid || "") === pid) identite = id;
    } catch (e) { /* identité optionnelle dans la photo */ }

    if (!commandes.length && !identite) return null;

    return encryptData({
        v: 1,
        serveurPid: pid,
        scope: scopeKey(soc, mag),
        exporteLe: Date.now(),
        identite,
        commandes,
    });
}

/**
 * Réhydrate le store local depuis un ciphertext produit par exporterStore.
 * N'écrase pas l'identité déjà présente. Retourne le nombre de commandes restaurées.
 */
async function importerStore(soc, mag, blob) {
    if (!blob) return 0;
    let snap = null;
    try {
        snap = decryptData(blob);
    } catch (e) {
        console.error("[srv] restauration store : blob illisible", e);
        return 0;
    }
    if (!snap || !Array.isArray(snap.commandes)) return 0;

    if (snap.identite && snap.identite.pid) {
        const dejaLa = await getIdentite();
        if (!dejaLa) { try { await saveIdentite(snap.identite); } catch (e) { /* best-effort */ } }
    }

    let n = 0;
    for (const c of snap.commandes) {
        if (c && c.id) { await saveCommande(soc, mag, c); n++; }
    }
    return n;
}

export {
    saveIdentite,
    getIdentite,
    clearIdentite,

    saveCommande,
    getCommandes,
    getCommande,
    supprimerCommande,
    clearCommandes,
    purgerScopesObsoletes,

    exporterStore,
    importerStore,

    prochainNumeroAttente,

    STORE_IDENTITE,
    STORE_COMMANDES,
};
